import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { LuX } from "react-icons/lu";

import CuttingPickerCard, {
  CardQtyRow,
  CardRateRow,
  rowLabelClass,
} from "../cuttingLines/CuttingPickerCard";
import CuttingPickerDialog, {
  PickerFilter,
  matchesFilter,
  noFilter,
} from "../cuttingLines/CuttingPickerDialog";
import cx from "../ui/cx";
import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { cuttingLineKey, lineName } from "../../static/cuttingLine";
import { formatQty } from "../../static/materialLine";
import { isNumberSize } from "../../static/itemSize";
import { hasPermission, permission } from "../../static/permission";
import { rateText } from "../../static/processRate";
import { compactSelectStyles } from "../../static/styles";
import { CuttingRateSource } from "../../types/cuttingOrder";
import { ITurningLineInput } from "../../types/turningOrder";

// One size of a card (an item without sizes has one, without a name): what a line of it copies, the cut pieces ready
// for the order, and on an order being edited what its own line has received.
export type TurningCardSize = {
  key: string;
  size_id: number | null;
  sizeName: string | null;
  // Shown only; missing when the item page has none (turning needs none).
  cutLength: number | null;
  // Only for a user who may see rates.
  rate?: number;
  rateSource?: CuttingRateSource;
  // Pieces still to come on the party's other open orders.
  pending: number;
  // Cut pieces ready for this order (an order being edited counts its own lines as ready).
  ready: number;
  // On an order being edited: its own line's qty as saved, what it has received, and whether a receive closed it.
  savedQty: number;
  received: number;
  closed: boolean;
};

// One card per item with cut pieces ready that the party can be asked to turn, with its sizes, plus, on an order being
// edited, its own lines that are no longer offered. The form works them out; the picker only shows and picks them.
export type TurningCardProps = {
  key: string;
  item_id: number;
  // The item's category (0 without one): the form opens the picker on one category's cards.
  categoryId: number;
  code: string;
  // The parent's code for a sub item, else the item's description.
  subtitle: string | null;
  // The page whose item holds it: a sub item is edited on its parent's page.
  editItemId: number;
  imageUrl: string | null;
  materialName: string | null;
  sizes: TurningCardSize[];
};

type SizeOption = { value: string; label: string; size: TurningCardSize };

// The app's select sized like the card's boxes; its menu opens over the dialog.
const cardSelectStyles = compactSelectStyles<SizeOption>();

const noLines: ITurningLineInput[] = [];

// A line received against the order being edited stays on it, with its size.
const isReceived = (size: TurningCardSize) => size.received > 0 || size.closed;

// The most a line of this size may have: the cut pieces ready, or what it already had or received (the server lets a
// line keep those).
export const turningCap = (ready: number, savedQty: number, received: number) =>
  Math.max(ready, savedQty, received);

const isOverCap = (size: TurningCardSize, qty: string | undefined) =>
  Number(qty) > turningCap(size.ready, size.savedQty, size.received);

// The ready line of a card or a size: green, red once a typed qty is above what is ready.
export const readyLineClass = (over: boolean) =>
  cx(
    "text-xs font-semibold leading-5",
    over ? "text-red-600" : "text-green-700"
  );

// A size in the select, with the size unit: "120mm". A size still written as text before sizes became numbers is shown
// as it was written.
const sizeLabel = (size: TurningCardSize, unit: string) =>
  !size.sizeName
    ? "સાઈઝ વગર"
    : isNumberSize(size.sizeName)
    ? `${size.sizeName}${unit}`
    : size.sizeName;

type EditorProps = {
  card: TurningCardProps;
  // The size being edited, and its line once it has a qty.
  size: TurningCardSize;
  line: ITurningLineInput | undefined;
  rate: string;
  // An item with more sizes picks the size to edit here, from these.
  withSelect: boolean;
  sizeOptions: SizeOption[];
  withRates: boolean;
  qtyRef: React.Ref<HTMLInputElement>;
  onSelectSize: (key: string) => void;
  onChangeRate: (rate: string) => void;
  onChangeQty: (qty: string) => void;
};

// The card's editor: on an item with more sizes the size being edited, its pieces still pending and its cut pieces ready,
// then (for a user who may see rates) its rate, which starts at the size's rate, and its qty.
const SizeEditor = ({
  card,
  size,
  line,
  rate,
  withSelect,
  sizeOptions,
  withRates,
  qtyRef,
  onSelectSize,
  onChangeRate,
  onChangeQty,
}: EditorProps) => {
  const name = lineName(card.code, size.sizeName);
  const isLocked = !!line && isReceived(size);

  return (
    <div className="space-y-2">
      {/* On a narrow card (phones) the select goes under its label rather than shrinking, so the size stays readable. */}
      {withSelect && (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span className={rowLabelClass}>સાઈઝ:</span>
          <span className="relative ml-auto w-28 shrink-0">
            <Select<SizeOption, false>
              aria-label={`${card.code} સાઈઝ`}
              options={sizeOptions}
              value={
                sizeOptions.find((option) => option.value === size.key) || null
              }
              onChange={(option) => {
                if (option && option.value !== size.key) {
                  onSelectSize(option.value);
                }
              }}
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              styles={cardSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </span>
        </div>
      )}

      {withRates && (
        <CardRateRow
          label={`${name} ભાવ`}
          value={rate}
          disabled={isLocked}
          title={isLocked ? "મળી ગયેલ આઈટમ નો ભાવ બદલી શકાશે નહીં" : undefined}
          onChange={onChangeRate}
        />
      )}

      {withSelect && size.pending > 0 && (
        <div className="text-xs font-semibold leading-5 text-orange-700">
          ઓર્ડર બાકી: {formatQty(size.pending)}
        </div>
      )}

      {withSelect && (
        <div className={readyLineClass(isOverCap(size, line?.qty))}>
          તૈયાર: {formatQty(size.ready)}
        </div>
      )}

      {isLocked && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className={rowLabelClass}>મળેલ:</span>
          <span className="text-sm leading-4 text-gray-600">
            {formatQty(size.received)}
            {size.closed && " • બંધ"}
          </span>
        </div>
      )}

      <CardQtyRow
        label={`${name} જથ્થો`}
        value={line?.qty ?? ""}
        inputRef={qtyRef}
        onChange={onChangeQty}
      />
    </div>
  );
};

type AddedProps = {
  card: TurningCardProps;
  rows: ITurningLineInput[];
  sizeByKey: Map<string, TurningCardSize>;
  activeKey: string;
  sizeUnit: string;
  onSelect: (key: string) => void;
  onRemove: (key: string) => void;
};

// The sizes the card puts on the order, as "120mm × 100", at its end. A click edits one in the editor; the × takes it
// off the order, except a received one.
const AddedSizes = ({
  card,
  rows,
  sizeByKey,
  activeKey,
  sizeUnit,
  onSelect,
  onRemove,
}: AddedProps) => (
  <div className="flex flex-wrap gap-1.5 border-t border-dashed border-gray-300 pt-2.5">
    {rows.map((line) => {
      const key = cuttingLineKey(line);
      const size = sizeByKey.get(key)!;
      const name = lineName(card.code, size.sizeName);
      const canRemove = !isReceived(size);
      const isActive = key === activeKey;

      return (
        <span
          key={key}
          className={cx(
            "inline-flex items-center rounded-full border text-xs font-semibold leading-5",
            isActive
              ? "border-[#113F67] bg-[#113F67] text-white"
              : "border-gray-300 bg-white text-[#113F67]"
          )}
        >
          <button
            type="button"
            title="આ સાઈઝ બદલો"
            aria-label={`${name} બદલો`}
            onClick={() => onSelect(key)}
            className={cx("py-0.5 pl-2", canRemove ? "pr-1" : "pr-2")}
          >
            {sizeLabel(size, sizeUnit)} × {line.qty ? formatQty(line.qty) : "-"}
          </button>
          {canRemove && (
            <button
              type="button"
              title="આ સાઈઝ કાઢી નાખો"
              aria-label={`${name} કાઢી નાખો`}
              onClick={() => onRemove(key)}
              className={cx(
                "mr-1 rounded-full p-0.5",
                isActive
                  ? "text-white/80 hover:bg-white/20 hover:text-white"
                  : "text-gray-400 hover:bg-red-50 hover:text-red-600"
              )}
            >
              <LuX className="h-3 w-3" />
            </button>
          )}
        </span>
      );
    })}
  </div>
);

type CardProps = {
  card: TurningCardProps;
  // The item's lines on the picker, in the form's order.
  lines: ITurningLineInput[];
  withRates: boolean;
  sizeUnit: string;
  canOpenItem: boolean;
  onAddLine: (
    itemId: number,
    sizeId: number | null,
    rate: string,
    qty: string
  ) => void;
  onRemoveLines: (keys: string[]) => void;
  onChangeRate: (key: string, rate: string) => void;
  onChangeQty: (key: string, qty: string) => void;
};

// A card is drawn again only when something it gets changes. Its lines count as unchanged while each line is the same,
// so typing in one card doesn't redraw the others.
const sameCardProps = (prev: CardProps, next: CardProps) =>
  (Object.keys(next) as (keyof CardProps)[]).every((name) =>
    name === "lines"
      ? prev.lines.length === next.lines.length &&
        prev.lines.every((line, index) => line === next.lines[index])
      : prev[name] === next[name]
  );

// One card per item, like the cutting order picker's card. Ticking it opens its editor on its first size; an item with
// more sizes picks the size to edit there. A size goes on the order once it has a qty, and comes off when its qty is
// cleared; the sizes on the order are listed at the end of the card. Every size it shows has cut pieces ready, so all
// can be picked.
const ItemCard = React.memo(
  ({
    card,
    lines,
    withRates,
    sizeUnit,
    canOpenItem,
    onAddLine,
    onRemoveLines,
    onChangeRate,
    onChangeQty,
  }: CardProps) => {
    const sizeByKey = useMemo(
      () => new Map(card.sizes.map((size) => [size.key, size])),
      [card]
    );

    // The size select's options.
    const sizeOptions = useMemo(
      () =>
        card.sizes.map(
          (size): SizeOption => ({
            value: size.key,
            label: sizeLabel(size, sizeUnit),
            size,
          })
        ),
      [card, sizeUnit]
    );

    // The item's lines of a size the card doesn't have (the party doesn't offer it) stay off it, as before.
    const rows = lines.filter((line) => sizeByKey.has(cuttingLineKey(line)));
    const lineByKey = new Map(rows.map((line) => [cuttingLineKey(line), line]));

    const single = card.sizes.length === 1 ? card.sizes[0] : null;
    const firstSize = card.sizes[0];
    const isSelected = rows.length > 0;
    const isLocked = rows.some((line) =>
      isReceived(sizeByKey.get(cuttingLineKey(line))!)
    );
    const pending = card.sizes.reduce((sum, size) => sum + size.pending, 0);
    const ready = card.sizes.reduce((sum, size) => sum + size.ready, 0);
    const isOver = rows.some((line) =>
      isOverCap(sizeByKey.get(cuttingLineKey(line))!, line.qty)
    );

    // An open card shows its editor: one with lines opens with them, and ticking opens one.
    const [isOpen, setIsOpen] = useState(isSelected);
    // The size in the editor: the one picked, else the first line's, else the first.
    const [active, setActive] = useState<string | null>(null);
    // A rate typed for a size before its qty, or kept when its qty was cleared, by size.
    const [drafts, setDrafts] = useState<Record<string, string>>({});

    const activeKey =
      active && sizeByKey.has(active)
        ? active
        : rows.length > 0
        ? cuttingLineKey(rows[0])
        : firstSize?.key ?? null;
    const activeSize = activeKey ? sizeByKey.get(activeKey) : undefined;
    const activeLine = activeKey ? lineByKey.get(activeKey) : undefined;
    const activeRate = activeLine
      ? activeLine.rate
      : activeKey && drafts[activeKey] !== undefined
      ? drafts[activeKey]
      : rateText(activeSize?.rate);

    const showEditor = (isOpen || isSelected) && !!activeSize;
    const canToggle = showEditor ? !isLocked : !!firstSize;

    const qtyRef = useRef<HTMLInputElement>(null);
    // Set when the user opens the card or picks a size, so the qty box takes the focus once it is there. Cards open
    // when the picker opens leave the focus alone.
    const focusQty = useRef(false);

    useEffect(() => {
      if (focusQty.current && qtyRef.current) {
        focusQty.current = false;
        qtyRef.current.focus();
      }
    });

    const handleToggle = () => {
      if (!canToggle) return;

      if (showEditor) {
        onRemoveLines(rows.map(cuttingLineKey));
        setIsOpen(false);
        setActive(null);
        setDrafts({});
      } else {
        focusQty.current = true;
        setIsOpen(true);
        setActive(firstSize?.key ?? null);
      }
    };

    const handleSelectSize = (key: string) => {
      focusQty.current = true;
      setActive(key);
    };

    // Keeps a line's rate for when its size is given a qty again.
    const keepRate = (key: string) => {
      const line = lineByKey.get(key);

      if (line) setDrafts((current) => ({ ...current, [key]: line.rate }));
    };

    // The first qty puts the size on the order, at the rate in the box; clearing it takes the size off again, except
    // a received line.
    const handleQty = (qty: string) => {
      if (!activeKey || !activeSize) return;

      if (!activeLine) {
        if (qty !== "") {
          onAddLine(card.item_id, activeSize.size_id, activeRate, qty);
        }
      } else if (qty === "" && !isReceived(activeSize)) {
        keepRate(activeKey);
        onRemoveLines([activeKey]);
      } else {
        onChangeQty(activeKey, qty);
      }
    };

    const handleRate = (rate: string) => {
      if (!activeKey) return;

      if (activeLine) {
        onChangeRate(activeKey, rate);
      } else {
        setDrafts((current) => ({ ...current, [activeKey]: rate }));
      }
    };

    const handleRemove = (key: string) => {
      keepRate(key);
      onRemoveLines([key]);
    };

    return (
      <CuttingPickerCard
        code={card.code}
        sizeName={single?.sizeName ?? null}
        subtitle={card.subtitle}
        imageUrl={card.imageUrl}
        editItemId={card.editItemId}
        canOpenItem={canOpenItem}
        materialName={card.materialName}
        cutLength={single ? single.cutLength : null}
        lengthMissing={false}
        sizeUnit={sizeUnit}
        rate={single && withRates ? single.rate : undefined}
        rateSource={single?.rateSource}
        extra={
          <>
            {pending > 0 && (
              <div className="text-xs font-semibold leading-5 text-orange-700">
                ઓર્ડર બાકી: {formatQty(pending)}
              </div>
            )}
            <div className={readyLineClass(isOver)}>
              તૈયાર: {formatQty(ready)}
            </div>
          </>
        }
        isOpen={showEditor}
        canToggle={canToggle}
        title={isLocked ? "મળી ગયેલ આઈટમ કાઢી શકાશે નહીં" : undefined}
        onToggle={handleToggle}
        editor={
          showEditor &&
          activeKey &&
          activeSize && (
            <>
              <SizeEditor
                card={card}
                size={activeSize}
                line={activeLine}
                rate={activeRate}
                withSelect={!single}
                sizeOptions={sizeOptions}
                withRates={withRates}
                qtyRef={qtyRef}
                onSelectSize={handleSelectSize}
                onChangeRate={handleRate}
                onChangeQty={handleQty}
              />

              {!single && rows.length > 0 && (
                <AddedSizes
                  card={card}
                  rows={rows}
                  sizeByKey={sizeByKey}
                  activeKey={activeKey}
                  sizeUnit={sizeUnit}
                  onSelect={handleSelectSize}
                  onRemove={handleRemove}
                />
              )}
            </>
          )
        }
      />
    );
  },
  sameCardProps
);

ItemCard.displayName = "ItemCard";

type Props = {
  cards: TurningCardProps[];
  cardsLoading: boolean;
  // The category the cards belong to, after the title.
  categoryLabel?: string;
  // The form's lines when the picker opens; the picker hands its lines back when it closes.
  initialLines: ITurningLineInput[];
  // Rates show only for a user who may see them.
  withRates: boolean;
  // An in-house party is offered items without a turning rate too.
  inhouse: boolean;
  sizeUnit: string;
  onClose: (lines: ITurningLineInput[]) => void;
};

// Picks the cut pieces to turn as cards, like the cutting order picker: one card per item, and a line per size given a
// qty in its card. The form opens it on one category's cards; the lines of the order's other categories pass through
// untouched. There is no separate save; closing the picker in any way hands its lines to the form. Mount it only while
// it is open.
const TurningItemPickerModal = ({
  cards,
  cardsLoading,
  categoryLabel,
  initialLines,
  withRates,
  inhouse,
  sizeUnit,
  onClose,
}: Props) => {
  const loggingUser = useAppSelector(getLoggingUser);
  const canOpenItem = hasPermission(
    loggingUser?.permissions,
    permission.itemsUpdate
  );

  const [lines, setLines] = useState<ITurningLineInput[]>(() => initialLines);
  // The filter in use: it changes only on "ફિલ્ટર લાગુ કરો" (or Enter).
  const [filter, setFilter] = useState<PickerFilter>(noFilter);

  // The items already on the form come first. They keep their place while picking, so no card moves under the pointer.
  const [openedWith] = useState(
    () => new Set(initialLines.map((line) => line.item_id))
  );

  const linesByItem = useMemo(() => {
    const byItem = new Map<number, ITurningLineInput[]>();

    lines.forEach((line) =>
      byItem.set(line.item_id, [...(byItem.get(line.item_id) || []), line])
    );

    return byItem;
  }, [lines]);

  // A new line comes right after the same item's other lines, so its sizes stay together on the form. It comes with its
  // first qty and the rate in the card's box.
  const handleAddLine = useCallback(
    (itemId: number, sizeId: number | null, rate: string, qty: string) => {
      setLines((current) => {
        const line: ITurningLineInput = {
          item_id: itemId,
          size_id: sizeId,
          qty,
          rate,
          refresh: false,
        };
        const key = cuttingLineKey(line);

        if (current.some((item) => cuttingLineKey(item) === key)) {
          return current;
        }

        const last = current.map((item) => item.item_id).lastIndexOf(itemId);

        return last === -1
          ? [...current, line]
          : [...current.slice(0, last + 1), line, ...current.slice(last + 1)];
      });
    },
    []
  );

  const handleRemoveLines = useCallback((keys: string[]) => {
    setLines((current) =>
      current.filter((line) => !keys.includes(cuttingLineKey(line)))
    );
  }, []);

  const handleChangeRate = useCallback((key: string, rate: string) => {
    setLines((current) =>
      current.map((line) =>
        cuttingLineKey(line) === key ? { ...line, rate } : line
      )
    );
  }, []);

  const handleChangeQty = useCallback((key: string, qty: string) => {
    setLines((current) =>
      current.map((line) =>
        cuttingLineKey(line) === key ? { ...line, qty } : line
      )
    );
  }, []);

  // The cards already on the form first, then the rest, as the filter finds them.
  const visibleCards = useMemo(() => {
    const found = cards.filter((card) =>
      matchesFilter(filter, card.item_id, card.code)
    );

    return [
      ...found.filter((card) => openedWith.has(card.item_id)),
      ...found.filter((card) => !openedWith.has(card.item_id)),
    ];
  }, [cards, openedWith, filter]);

  // The lines of the cards shown: the order's other categories don't count here.
  const pickedCount = useMemo(() => {
    const itemIds = new Set(cards.map((card) => card.item_id));

    return lines.filter((line) => itemIds.has(line.item_id)).length;
  }, [cards, lines]);

  const handleClose = () => onClose(lines);

  return (
    <CuttingPickerDialog
      categoryLabel={categoryLabel}
      onFilter={setFilter}
      isLoading={cardsLoading && cards.length === 0}
      emptyLabel={
        visibleCards.length > 0
          ? null
          : cards.length === 0
          ? inhouse
            ? "ટર્નિંગ માટે કોઈ કટીંગ તૈયાર આઈટમ નથી, પહેલા કટીંગ રિસીવ કરો"
            : "આ પાર્ટી માટે ટર્નિંગ ભાવ વાળી કોઈ કટીંગ તૈયાર આઈટમ નથી, પહેલા કટીંગ રિસીવ કરો અથવા આઈટમ કે પાર્ટી ના ભાવ માં ટર્નિંગ ભાવ ઉમેરો"
          : "ફિલ્ટર મુજબ કોઈ આઈટમ મળી નથી"
      }
      pickedCount={pickedCount}
      onClose={handleClose}
    >
      {visibleCards.map((card) => (
        <ItemCard
          key={card.key}
          card={card}
          lines={linesByItem.get(card.item_id) || noLines}
          withRates={withRates}
          sizeUnit={sizeUnit}
          canOpenItem={canOpenItem}
          onAddLine={handleAddLine}
          onRemoveLines={handleRemoveLines}
          onChangeRate={handleChangeRate}
          onChangeQty={handleChangeQty}
        />
      ))}
    </CuttingPickerDialog>
  );
};

export default TurningItemPickerModal;
